import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, CheckCircle, XCircle, AlertCircle, Search, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { useExecutionRecordsApi, ExecutionRecord } from '../apis/execution-records-apis';
import { useUsersApi } from '../apis/users-apis';
import { usePlaybooksApi } from '../apis/playbooks-apis';
import { UserData, Playbook } from '../types';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { PopoverRoot, PopoverTrigger, PopoverContent } from '../components/ui/Popover';
import { Calendar } from '../components/ui/Calendar';
import { LogViewerModal } from '../components/LogViewerModal';
import ToolsSelect from '../agents/ToolsSelect';

const AllExecutionsPage: React.FC = () => {
  const [records, setRecords] = useState<ExecutionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [userMap, setUserMap] = useState<Map<string, string>>(new Map());
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [playbooksLoading, setPlaybooksLoading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    playbookName: '',
    executionId: '',
    ownerId: '',
    status: '',
    dateFrom: null as Date | null,
    dateTo: null as Date | null,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filteredRecords, setFilteredRecords] = useState<ExecutionRecord[]>([]);
  
  // Log viewer modal state
  const [selectedLogUrl, setSelectedLogUrl] = useState<string | null>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedLogType, setSelectedLogType] = useState<'ai' | 'execution' | null>(null);
  
  const { getExecutionRecords } = useExecutionRecordsApi();
  const { getUsers } = useUsersApi();
  const { getPlaybooks } = usePlaybooksApi();

  // Fetch users to map owner IDs to emails
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersData = await getUsers();
        const userMap = new Map();
        usersData.forEach((user: UserData) => {
          userMap.set(user.userId, user.email);
        });
        setUserMap(userMap);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  // Fetch playbooks for the dropdown
  useEffect(() => {
    const fetchPlaybooks = async () => {
      setPlaybooksLoading(true);
      try {
        const getAllPlaybooks = true;
        const playbooksData = await getPlaybooks(getAllPlaybooks);
        setPlaybooks(playbooksData);
      } catch (error) {
        console.error('Error fetching playbooks:', error);
      } finally {
        setPlaybooksLoading(false);
      }
    };

    fetchPlaybooks();
  }, []);

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Check if any filters are active
        const hasFilters = hasActiveFilters();
        
        // Prepare filter parameters if filters are active
        const filterParams = hasFilters ? {
          playbookName: filters.playbookName.trim() || undefined,
          executionId: filters.executionId.trim() || undefined,
          ownerId: filters.ownerId.trim() || undefined,
          status: filters.status && filters.status !== 'none' ? filters.status : undefined,
          dateFrom: filters.dateFrom ? filters.dateFrom.toISOString() : null,
          dateTo: filters.dateTo ? filters.dateTo.toISOString() : null
        } : undefined;
        
        // Pass null as playbookId to get all execution records
        const response = await getExecutionRecords('', pageSize, currentPage, filterParams);
        setRecords(response.items);
        setFilteredRecords(response.items);
        setTotalRecords(response.metadata.scannedCount);
        setTotalPages(Math.ceil(response.metadata.scannedCount / pageSize));
      } catch (err) {
        console.error('Error fetching execution records:', err);
        setError('Failed to load execution records. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [currentPage, pageSize, filters]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const getStatusIcon = (result: string) => {
    if (!result) {
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
    
    switch (result.toLowerCase()) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'kill_switch':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const handleFilterChange = (field: keyof typeof filters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      playbookName: '',
      executionId: '',
      ownerId: '',
      status: '',
      dateFrom: null,
      dateTo: null,
    });
    // Reset to page 1 when clearing filters
    setCurrentPage(1);
  };

  const hasActiveFilters = () => {
    return filters.playbookName.trim() || 
           filters.executionId.trim() || 
           filters.ownerId.trim() ||
           (filters.status && filters.status !== 'none') || 
           filters.dateFrom !== null || 
           filters.dateTo !== null;
  };

  // Format date for display in the UI
  const formatDateDisplay = (date: Date | null): string => {
    if (!date) return '';
    return date.toLocaleDateString();
  };

  const handleOpenLogModal = (logUrl: string, type: 'ai' | 'execution') => {
    setSelectedLogUrl(logUrl);
    setSelectedLogType(type);
    setIsLogModalOpen(true);
  };

  const handleCloseLogModal = () => {
    setIsLogModalOpen(false);
    setSelectedLogUrl(null);
    setSelectedLogType(null);
  };

  // Create a custom component for playbook selection
  const PlaybookSelect: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredPlaybooks = searchQuery.length >= 1 ? playbooks.filter(pkg => {
      const searchLower = searchQuery.toLowerCase();
      return (
        pkg.playbookName.toLowerCase().includes(searchLower) ||
        (pkg.playbookDescription && pkg.playbookDescription.toLowerCase().includes(searchLower))
      );
    }) : [];

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      setIsDropdownOpen(value.length >= 1);
    };

    const handlePlaybookSelect = (playbookName: string) => {
      handleFilterChange('playbookName', playbookName);
      setSearchQuery('');
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Filter by playbook name"
            value={searchQuery || filters.playbookName}
            onChange={handleSearchChange}
            className="pl-10"
            disabled={playbooksLoading || !!filters.playbookName}
          />
          {filters.playbookName && (
            <button
              onClick={() => {
                handleFilterChange('playbookName', '');
                setSearchQuery('');
              }}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Dropdown Results */}
        {isDropdownOpen && searchQuery.length >= 1 && (
          <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
            {playbooksLoading ? (
              <div className="p-4 text-center text-gray-500">Loading playbooks...</div>
            ) : filteredPlaybooks.length > 0 ? (
              <div className="py-1">
                {filteredPlaybooks.map(pkg => (
                  <div
                    key={pkg.playbookId}
                    onClick={() => handlePlaybookSelect(pkg.playbookName)}
                    className={`
                      px-4 py-2 cursor-pointer hover:bg-gray-50
                      ${pkg.playbookName === filters.playbookName ? 'bg-blue-50' : ''}
                    `}
                  >
                    <div className="font-medium text-gray-900">{pkg.playbookName}</div>
                    {pkg.playbookDescription && (
                      <div className="text-sm text-gray-600 truncate" title={pkg.playbookDescription}>
                        {pkg.playbookDescription}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                No playbooks found matching your search
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Create a custom component for owner selection
  const OwnerSelect: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const ownerEntries = Array.from(userMap.entries());
    const filteredOwners = searchQuery.length >= 1 ? ownerEntries.filter(([userId, email]) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        email.toLowerCase().includes(searchLower) ||
        userId.toLowerCase().includes(searchLower)
      );
    }) : [];

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      setIsDropdownOpen(value.length >= 1);
    };

    const handleOwnerSelect = (ownerId: string) => {
      handleFilterChange('ownerId', ownerId);
      setSearchQuery('');
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Filter by owner email"
            value={searchQuery || (filters.ownerId ? userMap.get(filters.ownerId) || filters.ownerId : '')}
            onChange={handleSearchChange}
            className="pl-10"
            disabled={!!filters.ownerId}
          />
          {filters.ownerId && (
            <button
              onClick={() => {
                handleFilterChange('ownerId', '');
                setSearchQuery('');
              }}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Dropdown Results */}
        {isDropdownOpen && searchQuery.length >= 1 && (
          <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
            {filteredOwners.length > 0 ? (
              <div className="py-1">
                {filteredOwners.map(([userId, email]) => (
                  <div
                    key={userId}
                    onClick={() => handleOwnerSelect(userId)}
                    className={`
                      px-4 py-2 cursor-pointer hover:bg-gray-50
                      ${userId === filters.ownerId ? 'bg-blue-50' : ''}
                    `}
                  >
                    <div className="font-medium text-gray-900">{email}</div>
                    <div className="text-sm text-gray-600">User ID: {userId}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-500">
                No owners found matching your search
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl">All Execution Records</CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1"
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters() && (
                <span className="ml-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showFilters && (
            <div className="mb-6 p-4 border rounded-md bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium">Filter Execution Records</h3>
                {hasActiveFilters() && (
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={clearFilters}
                    className="bg-red-500 hover:bg-red-600 text-white flex items-center"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear Filters
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Playbook Name</label>
                  <PlaybookSelect />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Execution ID</label>
                  <Input
                    placeholder="Filter by execution ID"
                    value={filters.executionId}
                    onChange={(e) => handleFilterChange('executionId', e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Owner Email</label>
                  <OwnerSelect />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Status</label>
                  <Select
                    value={filters.status || ''}
                    onValueChange={(value) => handleFilterChange('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All Statuses</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                      <SelectItem value="error">Error</SelectItem>
                      <SelectItem value="kill_switch">Kill Switch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* todo : Date picker filters - commented out for now, will be worked on later
                <div>
                  <label className="text-sm font-medium mb-1 block">Date From</label>
                  <PopoverRoot>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {filters.dateFrom ? (
                          formatDateDisplay(filters.dateFrom)
                        ) : (
                          <span className="text-muted-foreground">Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.dateFrom || undefined}
                        onSelect={(date: Date | undefined) => handleFilterChange('dateFrom', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </PopoverRoot>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Date To</label>
                  <PopoverRoot>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        {filters.dateTo ? (
                          formatDateDisplay(filters.dateTo)
                        ) : (
                          <span className="text-muted-foreground">Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={filters.dateTo || undefined}
                        onSelect={(date: Date | undefined) => handleFilterChange('dateTo', date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </PopoverRoot>
                </div>
                */}
              </div>
            </div>
          )}
          
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <Spinner />
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-700 rounded-md">
              <p>{error}</p>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-4 bg-gray-50 text-gray-500 rounded-md text-center">
              {hasActiveFilters() 
                ? "No execution records match your filters. Try adjusting your filters or clear them."
                : "No execution records found."}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-full">
                <Table className="w-full table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/7 text-center">Timestamp</TableHead>
                      <TableHead className="w-1/7 text-center">Status</TableHead>
                      <TableHead className="w-1/7 text-center">Playbook Name</TableHead>
                      <TableHead className="w-1/7 text-center">Execution ID</TableHead>
                      <TableHead className="w-1/7 text-center">Owner</TableHead>
                      <TableHead className="w-1/7 text-center">AI Log</TableHead>
                      <TableHead className="w-1/7 text-center">Execution Log</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecords.map((record) => (
                      <TableRow key={record?.executionId || 'unknown'}>
                        <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                          {formatDate(record?.executionDate)}
                        </td>
                        <td className="px-2 py-2 text-sm truncate text-center">
                          <div className="flex items-center justify-center">
                            {getStatusIcon(record?.result)}
                            <span className="ml-1 truncate">{record?.result || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                          {record?.playbookName || 'N/A'}
                        </td>
                        <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                          {record?.executionId || 'N/A'}
                        </td>
                        <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                          {record?.ownerId ? userMap.get(record.ownerId) || record.ownerId : 'N/A'}
                        </td>
                        <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                          {record?.aiLog ? (
                            <button 
                              onClick={() => handleOpenLogModal(record.aiLog!, 'ai')}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              View AI Log
                            </button>
                          ) : 'N/A'}
                        </td>
                        <td className="px-2 py-2 text-sm text-gray-500 truncate text-center">
                          {record?.executionLog ? (
                            <button 
                              onClick={() => handleOpenLogModal(record.executionLog!, 'execution')}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              View Execution Log
                            </button>
                          ) : 'N/A'}
                        </td>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRecords)}</span> of{' '}
                  <span className="font-medium">{totalRecords}</span> records
                  {hasActiveFilters() && (
                    <span className="ml-2 text-blue-600">
                      (filtered)
                    </span>
                  )}
                </p>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {selectedLogUrl && selectedLogType && (
        <LogViewerModal
          logUrl={selectedLogUrl}
          isOpen={isLogModalOpen}
          onClose={handleCloseLogModal}
          title={selectedLogType === 'ai' ? "AI Log Viewer" : "Execution Log Viewer"}
          type={selectedLogType}
        />
      )}
    </div>
  );
};

export default AllExecutionsPage; 